import React, {useCallback, useRef, useState} from 'react';
import {useWindowDimensions} from 'react-native';
import {ScrollView} from 'react-native-gesture-handler';
import YoutubePlayer, {PLAYER_STATES} from 'react-native-youtube-iframe';
import analytics from '@react-native-firebase/analytics';

import {Header} from '../../components/Interface/Headers/Header';
import {Titles} from '../../components/Interface/Typographies/Titles';

import {
  Container,
  Content,
  ScrollContent,
  PlayerWrapper,
  VideoInfoContainer,
  LessonTag,

  SectionTitle,
  LessonList,
  LessonItem,
  LessonThumbWrapper,
  LessonThumb,
  ThumbOverlay,
  PlayButton,
  PlayIcon,
  DurationBadge,
  DurationText,
  LessonInfo,
  LessonNumber,
  LessonItemTitle,
  LessonTagContainer,
} from './styles';

import comoFazerAPrimeiraCompra from '../../assets/images/banners/Como-fazer-a-primeira-compra.webp';
import comoIndicarEGanhar from '../../assets/images/banners/Como-indicar-amigos.webp';
import comoGirarEGanhar from '../../assets/images/banners/Como-girar-e-ganhar.webp';
import comoFazerCheckin from '../../assets/images/banners/Como-fazer-checkin.webp';
import comoAplicarCupom from '../../assets/images/banners/Como-ativar-cupons-de-desconto.webp';
import comoAvaliarEGanhar from '../../assets/images/banners/Como-avaliar.webp';

interface ILesson {
  id: string;
  videoId: string;
  title: string;
  description: string;
  duration: string;
  image: number;
}

const LESSONS: ILesson[] = [
  {
    id: 'primeira-compra',
    videoId: 'YvoeFT5oTAM',
    title: 'Como fazer a primeira compra',
    description:
      'Veja o passo a passo para fazer sua primeira compra de combustível no app.',
    duration: '1:49',
    image: comoFazerAPrimeiraCompra,
  },
  {
    id: 'indicar-ganhar',
    videoId: 'bTpoSopLdpU',
    title: 'Como indicar e ganhar',
    description:
      'Descubra como indicar amigos para o Baratão e ganhar recompensas.',
    duration: '0:54',
    image: comoIndicarEGanhar,
  },
  {
    id: 'girar-ganhar',
    videoId: '7uYUTnuj9K0',
    title: 'Como girar e ganhar',
    description:
      'Aprenda a usar o Gire e Ganhe e acumule prêmios todos os dias.',
    duration: '0:48',
    image: comoGirarEGanhar,
  },
  {
    id: 'checkin',
    videoId: 'edatAQTudDk',
    title: 'Como fazer check-in',
    description: 'Entenda como funciona o check-in diário e ganhe prêmios.',
    duration: '0:44',
    image: comoFazerCheckin,
  },
  {
    id: 'aplicar-cupom',
    videoId: 'ec60yYbdWWs',
    title: 'Como aplicar cupom',
    description: 'Veja como aplicar cupons de desconto na hora de abastecer.',
    duration: '1:31',
    image: comoAplicarCupom,
  },
  {
    id: 'avaliar-ganhar',
    videoId: 'YbyvVUAu_2A',
    title: 'Como avaliar e ganhar',
    description: 'Avalie o app e ganhe cupons de desconto exclusivos.',
    duration: '0:45',
    image: comoAvaliarEGanhar,
  },
];

export function Tutoriais() {
  const {width} = useWindowDimensions();
  const playerWidth = width - 48;
  const playerHeight = playerWidth * (9 / 16);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [playing, setPlaying] = useState(true);

  const scrollRef = useRef<ScrollView>(null);

  const currentLesson = LESSONS[currentIndex];

  function handleSelectLesson(index: number) {
    if (index !== currentIndex) {
      analytics().logEvent('clica_aula_tutorial_como_comprar', {
        aula: LESSONS[index].id,
      });
      setCurrentIndex(index);
    }

    setPlaying(true);
    scrollRef.current?.scrollTo({y: 0, animated: true});
  }

  const handleChangeState = useCallback(
    (state: PLAYER_STATES) => {
      if (state === PLAYER_STATES.ENDED) {
        setPlaying(false);
        if (currentIndex < LESSONS.length - 1) {
          setCurrentIndex(currentIndex + 1);
          setPlaying(true);
        }
        return;
      }

      if (state === PLAYER_STATES.PLAYING) {
        setPlaying(true);
      }

      if (state === PLAYER_STATES.PAUSED) {
        setPlaying(false);
      }
    },
    [currentIndex],
  );

  return (
    <Container>
      <Header icon="chevron-left"  />
      <Content>
        <ScrollContent ref={scrollRef} showsVerticalScrollIndicator={false}>
          <Titles
            title="Como usar o Baratão"
            subtitle="Assista as aulas e aprenda a aproveitar tudo que o app oferece."
          />

          <PlayerWrapper style={{height: playerHeight}}>
            <YoutubePlayer
              key={currentLesson.id}
              height={playerHeight}
              width={playerWidth}
              videoId={currentLesson.videoId}
              play={playing}
              onChangeState={handleChangeState}
              forceAndroidAutoplay
              webViewProps={{
                allowsInlineMediaPlayback: true,
                mediaPlaybackRequiresUserAction: false,
              }}
            />
          </PlayerWrapper>

          <VideoInfoContainer>
            <SectionTitle>Todas as aulas</SectionTitle>
            <LessonTagContainer>
              <LessonTag>
                Aula {currentIndex + 1} de {LESSONS.length}
              </LessonTag>
            </LessonTagContainer>
          </VideoInfoContainer>

          <LessonList>
            {LESSONS.map((lesson, index) => {
              const active = index === currentIndex;

              return (
                <LessonItem
                  key={lesson.id}
                  active={active}
                  activeOpacity={0.7}
                  onPress={() => handleSelectLesson(index)}>
                  <LessonThumbWrapper>
                    <LessonThumb source={lesson.image} resizeMode="cover" />
                    <ThumbOverlay>
                      <PlayButton active={active}>
                        <PlayIcon
                          active={active}
                          name={active && playing ? 'pause' : 'play'}
                        />
                      </PlayButton>
                    </ThumbOverlay>
                    <DurationBadge>
                      <DurationText>{lesson.duration}</DurationText>
                    </DurationBadge>
                  </LessonThumbWrapper>

                  <LessonInfo>
                    <LessonNumber>Aula {index + 1}</LessonNumber>
                    <LessonItemTitle active={active} numberOfLines={2}>
                      {lesson.title}
                    </LessonItemTitle>
                  </LessonInfo>
                </LessonItem>
              );
            })}
          </LessonList>
        </ScrollContent>
      </Content>
    </Container>
  );
}
